import { Monitor, MonitorSmartphone, Smartphone } from 'lucide-react'
import { useCallback, useState } from 'react'
import type { Device, DevicePlatform } from '../../api/devices'
import { emit } from '../../platform/analytics/analytics'
import { useScreenViewed } from '../../platform/analytics/useScreenViewed'
import { Async } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { useAuth } from '../../auth/useAuth'
import { ConfirmDialog } from '../../platform/primitives/Dialog'
import { Panel } from '../../platform/primitives/Panel'
import { Button } from '../../platform/primitives/Button'
import { useDevices } from './useDevices'
import { useSignOutDevice, useSignOutEverywhere } from './useDeviceSignOut'
import './devices.css'

const PLATFORM_LABEL: Record<DevicePlatform, string> = {
  web: 'Web browser',
  ios: 'iOS app',
  android: 'Android app',
}

function PlatformIcon({ platform }: { platform: DevicePlatform | null }) {
  const props = { size: 20, 'aria-hidden': true, className: 'devices__icon' } as const
  if (platform === 'web') return <Monitor {...props} />
  if (platform === 'ios' || platform === 'android') return <Smartphone {...props} />
  return <MonitorSmartphone {...props} />
}

const WHEN = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

/**
 * Owns its own sign-out mutation (a DELETE) rather than taking it as props: each row's
 * pending and error state is its own, and a per-row container would only forward them.
 * `Devices` is the screen; this is its one stateful part.
 */
function DeviceRow({ device }: { device: Device }) {
  const signOut = useSignOutDevice()
  const detail = [device.platform ? PLATFORM_LABEL[device.platform] : null, device.place]
    .filter(Boolean)
    .join(' · ')
  return (
    <li className="devices__row">
      <PlatformIcon platform={device.platform} />
      <div className="devices__text">
        <span className="devices__name">
          {device.name}
          {device.isCurrent ? <span className="devices__badge">This device</span> : null}
        </span>
        {detail ? <span className="devices__meta">{detail}</span> : null}
        <span className="devices__meta">
          {device.isCurrent ? 'Active now' : `Last seen ${WHEN.format(device.lastSeenAt)}`}
        </span>
      </div>
      {device.isCurrent ? null : (
        <div className="devices__action">
          <Button
            variant="outline"
            disabled={signOut.isPending}
            aria-label={`Sign out ${device.name}`}
            onClick={() => signOut.mutate(device.id)}
          >
            {signOut.isPending ? 'Signing out…' : 'Sign out'}
          </Button>
          {signOut.isError ? (
            <p className="devices__error" role="alert">
              We couldn’t sign this device out. It’s still signed in — try again.
            </p>
          ) : null}
        </div>
      )}
    </li>
  )
}

/**
 * Settings › Devices — M13. Starts at `<h2>`: `ChromeLayout` owns the `<h1>`.
 *
 * No IP address is rendered, and none reaches this component: `parseDevices`
 * drops it. Every row but this device can be signed out; "Sign out everywhere"
 * ends THIS session too and says so. Neither asks for step-up re-auth.
 */
export function Devices() {
  const state = useDevices()
  const auth = useAuth()
  const everywhere = useSignOutEverywhere()
  const [confirming, setConfirming] = useState(false)
  const fire = useCallback(
    (devices: readonly Device[]) =>
      emit('devices_viewed', {
        device_count: devices.length,
        current_device_present: devices.some((d) => d.isCurrent),
      }),
    [],
  )
  useScreenViewed(state, fire)

  return (
    <section className="devices" aria-labelledby="devices-title">
      <h2 className="devices__title" id="devices-title">
        Devices
      </h2>
      <p className="devices__lede">Where you’re signed in to TrackE5.</p>
      <Async state={state} surface="devices" skeleton={<Skeleton />} empty={{
          title: 'No devices yet',
          body: 'This browser is added shortly after you sign in.',
        }}>
        {(devices) => (
          <>
            {devices.some((d) => d.isCurrent) ? null : (
              <p className="devices__note">
                This browser isn’t on the list yet. It’s added shortly after you sign in.
              </p>
            )}
            <ul className="devices__list">
              {devices.map((d) => (
                <DeviceRow key={d.id} device={d} />
              ))}
            </ul>
          </>
        )}
      </Async>
      <Panel className="devices__foot">
        <Button variant="outline" onClick={() => setConfirming(true)}>
          Sign out everywhere
        </Button>
      </Panel>
      <ConfirmDialog
        open={confirming}
        mode="sign_out_everywhere"
        title="Sign out everywhere?"
        body="This signs you out on every device, including this one. You’ll need to sign in again here."
        confirmLabel="Sign out everywhere"
        pendingLabel="Signing out…"
        destructive
        onCancel={() => setConfirming(false)}
        onConfirm={async () => {
          await everywhere.mutateAsync()
          setConfirming(false)
          // The server has already revoked this session. The local sign-out runs
          // OUTSIDE the dialog's try — a port failure there must not be logged as a
          // failed confirmation of an action that succeeded — and `signOut` ends the
          // local session even when the port rejects. `keepReturnTo: false` keeps this
          // page from becoming where the next sign-in lands.
          void auth.signOut({ keepReturnTo: false }).catch(() => {})
        }}
        errorFor={() => 'We couldn’t sign you out everywhere, so nothing changed. Try again.'}
      />
    </section>
  )
}
